import { KonvaEventObject } from 'konva/types/Node';
import { Line as KLine } from 'konva/types/shapes/Line';
import { Vector2d } from 'konva/types/types';
import { getInvertedTransform } from 'utils/konva/drawingHelpers';

/**
 * Set cursor style
 */
export function setCursor(evt: KonvaEventObject<MouseEvent>, cursor: CSSStyleDeclaration['cursor']): void {
  const stage = evt.target.getStage();
  if (stage) {
    stage.container().style.cursor = cursor;
  }
}

/**
 * Dictates how close the click needs to be to the border. Increase to allow for more generous click zones.
 */
const BORDER_CLICK_DEVIATION = 8;

/**
 * Identifies if two numbers are close enough to each other
 */
export const isCloseEnough = (a: number, b: number, deviation = BORDER_CLICK_DEVIATION): boolean => {
  return Math.abs(a - b) <= deviation;
};

/**
 * Identifies if a mouse click is close enough to the border of the rectangle
 */
export const isNearRectBorder = (evt: KonvaEventObject<MouseEvent>): boolean => {
  const { x, y } = evt.target.getStage()?.getPointerPosition() as Vector2d;
  const rectPos = evt.target.getAbsolutePosition();
  const { width, height } = evt.target.getClientRect();
  const ptr = {
    x: Math.round(x),
    y: Math.round(y),
  };
  const border = {
    left: Math.round(rectPos.x),
    right: Math.round(rectPos.x + width),
    top: Math.round(rectPos.y),
    bottom: Math.round(rectPos.y + height),
  };

  return (
    isCloseEnough(ptr.x, border.left) || isCloseEnough(ptr.x, border.right) || isCloseEnough(ptr.y, border.top) || isCloseEnough(ptr.y, border.bottom)
  );
};

interface Point {
  x: number;
  y: number;
}
interface Line {
  m: number;
  b: number;
  points: [Point, Point];
}

/**
 * Konva line points are stored as a number array, this makes them easier to deal with
 * `[x1,y1,x2,y2...] --> [{x,y}, {x,y}...]`
 */
const convertToPointObjects = (points: number[]): Point[] => {
  const pts: Point[] = [];

  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];

    pts.push({
      x,
      y,
    });
  }

  return pts;
};

/**
 * Convert points to lines containing slope `m` and y-axis intercept `b` as well as start and end points
 */
const convertToLines = (points: Point[]): Line[] => {
  const lines: Line[] = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < points.length; i++) {
    const point1 = points[i];
    const point2 = points[i + 1] || points[0]; // last line is between last and first point

    const m = (point2.y - point1.y) / (point2.x - point1.x);
    const b = -m * point1.x + point1.y;

    lines.push({
      m,
      b,
      points: [point1, point2],
    });
  }

  return lines;
};

/**
 * Check if a point is near a line
 *
 * The point's x and y should be between the Line's x and y values.
 * Given the `m` slope of the line, the `b` values should match
 */
const isPointNearLine = (line: Line, point: Point): boolean => {
  const { m, b } = line;
  const [point1, point2] = line.points;
  const minX = Math.min(point1.x, point2.x);
  const maxX = Math.max(point1.x, point2.x);
  const minY = Math.min(point1.y, point2.y);
  const maxY = Math.max(point1.y, point2.y);

  const isWithinRange = point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
  if (!isWithinRange) return false;

  const pointB = -m * point.x + point.y;

  return isCloseEnough(pointB, b);
};

/**
 * Identifies if a mouse click is close enough to the border of the polygon
 */
export const isNearPolyBorder = (evt: KonvaEventObject<MouseEvent>): boolean => {
  const pointerPos = evt.target.getStage()?.getPointerPosition() as Vector2d;
  const points = convertToPointObjects((evt.target as KLine).points());
  const lines = convertToLines(points);

  // points are relatively positioned, so we have to apply the transform to the mouse pointer
  const { x, y } = getInvertedTransform(evt.target).point(pointerPos);

  // time for maths
  const close = Boolean(lines.some((line) => isPointNearLine(line, { x, y })));
  return close;
};
